/**
 * The Director — turns semantic Beats into a smooth, watchable performance.
 *
 * Responsibilities, in order of importance:
 *  1. LEGIBILITY  — every act holds long enough to read (min durations), even
 *     when events arrive in a burst; open-ended acts (a running tool, a long
 *     think) hold until the stream says they're done.
 *  2. SMOOTHNESS  — characters walk real paths between stations; consecutive
 *     same-work beats merge into one continuous act (chip shows ×N) instead of
 *     re-walking; a deep backlog speeds walks up and trims non-milestones
 *     rather than letting the theater lag minutes behind reality.
 *  3. TRUTH       — milestones (enter, waits, replies, specialist spawns,
 *     delivery, failure) are never dropped; the caption always states what the
 *     engine is actually doing.
 *  4. PHYSICALITY — nobody teleports and nothing pops into being. Specialists
 *     arrive through the door, walk to their bench and sit; they stand up and
 *     leave through the door when their work ends. Knowledge is fetched by
 *     walking to the library shelf; scene props (books pulled, crates stacked,
 *     parcel built) change when a character ARRIVES and handles them, not when
 *     the wire says so.
 *  5. COMPANY     — the workshop is a place people work IN, not a diagram.
 *     Long focus earns a coffee; a delegation is a door held open, a standup
 *     on the rug and a briefing handed over; bench work visibly PRODUCES
 *     crates that are built, lifted, carried and stacked one at a time; a
 *     finishing specialist hands their work back at the lead's desk before
 *     leaving; and in a lull people drift to the cooler, the planning board or
 *     a colleague's desk for a word.
 *
 * ── Interruption model ──────────────────────────────────────────────────────
 * The five systems above all need the same primitive: pause what you're doing,
 * do something else, come back and finish. That is `interrupt()` — it stashes
 * the running act (with the time it already served and whether the stream has
 * closed it), unshifts an "errand" onto the queue, and the errand's last act
 * carries `resumeStash` so finishing it restores the original. There is at
 * most ONE level of it: a character on an errand never starts another.
 *
 * The class is pure & deterministic: no Date.now(), no Math.random — the host
 * passes `now` into ingest/tick, so tests drive it with a fake clock.
 */

import { beats as enBeats } from '../i18n/messages/en/beats'
import { type BeatStrings, beatKey, MAIN_LANE } from './beats'
import { findPath, pathLength, pointAlong } from './iso'
import {
  BENCH_STATIONS,
  benchBriefSpot,
  besideSpot,
  CRATE_CAPACITY,
  DOOR_SPAWN,
  doorSpawnFor,
  faceToward,
  HUDDLE_CENTER,
  huddleSpot,
  inDoorZone,
  STATIONS,
  stationSlot,
  WORKBENCH_SLOTS
} from './stations'
import type {
  Act,
  Beat,
  BeatKind,
  CarriedItem,
  CharacterFrame,
  CharacterKind,
  EffectFrame,
  GridPoint,
  Mood,
  PoseId,
  SceneFlags,
  StationId,
  TheaterFrame
} from './types'

const WALK_SPEED_PX_S = 150
const CATCHUP_TAIL_MS = 5_000
const QUEUE_SOFT_CAP = 6
const SETTLE_AFTER_MS = 1_600
const SPECIALIST_SETTLE_MS = 1_400
/** Ambient "still at work" rotation while no act is queued mid-run. */
const AMBIENT_POSES: PoseId[] = ['read', 'think', 'write']
const AMBIENT_SLOT_MS = 8_000

/**
 * Continuous bench work that goes into one finished crate. Paced so a full
 * pallet takes about a minute of solid building: fast enough that the first
 * crate lands while you're still watching, slow enough that the stack reads as
 * earned rather than as a progress bar in disguise.
 */
const CRATE_BUILD_MS = 8_000
/** Uninterrupted focus a character manages before they need a coffee. */
const FATIGUE_MS = 72_000
/** Bodies at the coffee bar at once — more than this reads as a queue. */
const COFFEE_CAPACITY = 2
/** Quiet stretch before somebody invents something to do. */
const ERRAND_GAP_MS = 34_000
const ERRAND_IDLE_MS = 4_000
/** Simultaneous wanderers — the office must never look abandoned. */
const MAX_WANDERERS = 2
/** Safety net: a standup nobody closes disperses on its own. */
const HUDDLE_TIMEOUT_MS = 15_000
/** Fixed ring size so every huddle slot is stable and collision-free. */
const HUDDLE_RING = 5
const MOOD_FLASH_MS = 2_600

/** Acts a character may be pulled away from mid-performance. */
const INTERRUPTIBLE = new Set<BeatKind>(['think', 'write', 'tinker', 'call', 'file', 'fetch_books', 'orient'])

/** Errand acts — staging, never merged, never compressed away. */
const STAGING_KINDS = new Set<BeatKind>(['haul', 'break', 'errand', 'chat', 'hold_door', 'huddle', 'report', 'cheer'])

interface Stash {
  act: Act
  /** How much of the act's minMs was already served before the detour. */
  servedMs: number
  /** The stream closed the act while its owner was away. */
  closed: boolean
}

interface ActiveAct {
  act: Act
  phase: 'walk' | 'perform'
  walkFrom: GridPoint
  path: GridPoint[]
  walkStart: number
  walkMs: number
  performStart: number
  closed: boolean
  /** Where this act is performed — resolved ONCE so slot fanning, huddle
   *  spots and "beside whoever I'm reporting to" can't jitter mid-walk. */
  stand: { pos: GridPoint; facing: 1 | -1; yaw: number }
}

interface Performer {
  id: string
  kind: CharacterKind
  pos: GridPoint
  facing: 1 | -1
  yaw: number
  pose: PoseId
  /** Wall-clock the current pose started — drives one-shot gestures. */
  poseSince: number
  seated: boolean
  mood: Mood
  /** Wall-clock a transient mood (surprise, a flash of pride) expires. */
  moodUntil: number
  chip?: string
  bubble?: CharacterFrame['bubble']
  visible: boolean
  benchIndex?: number
  current: ActiveAct | null
  queue: Act[]
  idleSince: number
  lastActKind: BeatKind | null
  /** Stable 0..1 per-lane salt so nobody moves in lockstep. */
  seed: number
  /** The act an errand interrupted, waiting to be resumed. */
  stash: Stash | null
  /** Wall-clock this character next needs a break. */
  breakAt: number
  /** Wall-clock of their last ambient errand. */
  lastErrandAt: number
  /** Rotation counter so successive errands differ. */
  errandTick: number
  /** Somebody is talking TO them until this time. */
  socialUntil: number
  socialWith: string | null
}

interface ActSpec {
  pose: PoseId
  minMs: number
  openEnded?: boolean
  seated?: boolean
  bubble?: CharacterFrame['bubble']
}

/** How each beat kind performs at a station (main-lane stations shown). */
const SPECS: Partial<Record<BeatKind, ActSpec & { station: StationId }>> = {
  enter: { station: 'desk', pose: 'idle', minMs: 600 },
  orient: { station: 'desk', pose: 'read', minMs: 1800, seated: true },
  collect_mail: { station: 'inbox', pose: 'collect', minMs: 1800 },
  fetch_books: { station: 'shelf', pose: 'read', minMs: 2600 },
  sit_desk: { station: 'desk', pose: 'idle', minMs: 400, seated: true },
  think: { station: 'desk', pose: 'think', minMs: 2200, openEnded: true, seated: true, bubble: 'think' },
  write: { station: 'desk', pose: 'write', minMs: 2000, openEnded: true, seated: true },
  tinker: { station: 'workbench', pose: 'tinker', minMs: 2200, openEnded: true },
  call: { station: 'console', pose: 'call', minMs: 2200, openEnded: true, bubble: 'speech' },
  file: { station: 'cabinet', pose: 'file', minMs: 1600 },
  brief_specialist: { station: 'bench-0', pose: 'brief', minMs: 1800, bubble: 'speech' },
  stumble: { station: 'desk', pose: 'stumble', minMs: 1200 },
  stretch: { station: 'coffee', pose: 'sip', minMs: 2600 },
  wait_user: { station: 'window', pose: 'wait', minMs: 1600, openEnded: true, bubble: 'question' },
  user_note: { station: 'door', pose: 'collect', minMs: 1600 },
  pack: { station: 'delivery', pose: 'pack', minMs: 2600 },
  deliver: { station: 'delivery', pose: 'celebrate', minMs: 2800 },
  collapse: { station: 'desk', pose: 'droop', minMs: 3600, seated: true }
}

/**
 * Specialist repertoire. Desk work happens seated at their own bench; shared
 * resources (the library, the coffee bar, the cabinet) are real walks across
 * the room — and bench WORK is the workbench itself whenever a slot is free,
 * which is what puts specialists shoulder to shoulder building crates.
 * `station: null` means "your bench".
 */
const SPECIALIST_SPECS: Partial<Record<BeatKind, ActSpec & { station: StationId | null }>> = {
  think: { station: null, pose: 'think', minMs: 2000, openEnded: true, seated: true, bubble: 'think' },
  write: { station: null, pose: 'write', minMs: 1800, openEnded: true, seated: true },
  tinker: { station: 'workbench', pose: 'tinker', minMs: 2000, openEnded: true },
  call: { station: null, pose: 'call', minMs: 2000, openEnded: true, seated: true },
  stumble: { station: null, pose: 'stumble', minMs: 1100 },
  fetch_books: { station: 'shelf', pose: 'read', minMs: 2200 },
  stretch: { station: 'coffee', pose: 'sip', minMs: 2200 },
  file: { station: 'cabinet', pose: 'file', minMs: 1500 }
}

/** Bench-work fallback when every workbench slot is taken. */
const BENCH_FALLBACK: ActSpec = { pose: 'write', minMs: 2000, openEnded: true, seated: true }

export class Director {
  private performers = new Map<string, Performer>()
  private processed = new Set<string>()
  private pendingEffects: EffectFrame[] = []
  private effectSeq = 0
  private freeBenches: number[] = [0, 1, 2, 3]
  private benchByLane = new Map<string, number>()
  private overflowLanes = new Set<string>()
  /** Lanes whose run has ended — walking out or gone; excluded from "on it". */
  private retiredLanes = new Set<string>()
  private caption: string
  private terminal: 'success' | 'failure' | null = null
  private waiting = false
  private firstIngest = true
  private booksOut = 0
  private inboxCount = 0
  private parcelStage: SceneFlags['parcelStage'] = 0
  private shippedAtMs: number | null = null
  private entered = false
  /** Finished crates on the pallet beside the bench. */
  private crateStack = 0
  /** Paper output piling up on each bench desk. */
  private benchOutput = [0, 0, 0, 0]
  /** A delegation is in flight: arrivals get the door held + a standup. */
  private briefingArmed = false
  private huddleDeadline = 0
  private huddleLanes = new Set<string>()
  private readonly reducedMotion: boolean
  private readonly strings: BeatStrings

  constructor(opts?: { reducedMotion?: boolean; strings?: BeatStrings }) {
    this.reducedMotion = opts?.reducedMotion ?? false
    this.strings = opts?.strings ?? enBeats
    this.caption = this.strings.settingTheStage
  }

  /**
   * Feed the (cumulative) beat list. Already-seen beats are skipped, so the
   * host can pass the full re-derived array every render. On the very first
   * ingest, history older than CATCHUP_TAIL_MS is applied instantly — a page
   * opened mid-run shows the current tableau, then animates only the fresh
   * tail.
   */
  ingest(beats: Beat[], now: number): void {
    const instantBefore = this.firstIngest ? now - CATCHUP_TAIL_MS : Number.NEGATIVE_INFINITY
    this.firstIngest = false
    for (const beat of beats) {
      const key = beatKey(beat)
      if (this.processed.has(key)) continue
      this.processed.add(key)
      const instant = this.reducedMotion || beat.atMs < instantBefore
      this.accept(beat, now, instant)
    }
  }

  /** Advance the world and produce the render state for this animation frame. */
  tick(now: number): TheaterFrame {
    if (this.briefingArmed && now > this.huddleDeadline) this.disperseHuddle(now)

    for (const p of this.performers.values()) {
      const beforePose = p.pose
      this.advance(p, now)
      if (p.pose !== beforePose) p.poseSince = now
      this.applySocial(p, now)
      if (p.moodUntil > 0 && now > p.moodUntil && !this.terminal) {
        p.mood = 'neutral'
        p.moodUntil = 0
      }
    }

    const characters: CharacterFrame[] = []
    for (const p of this.performers.values()) {
      // You cannot walk TOWARD something already holding it: a `lift` act only
      // starts carrying once its owner has arrived and bent down. Everything
      // else (the walk to the pallet, to the packing table) carries the whole
      // way, walk included.
      const cur = p.current
      const carrying =
        cur?.act.carry && (cur.phase === 'perform' || cur.act.pose !== 'lift') ? cur.act.carry : undefined
      characters.push({
        id: p.id,
        kind: p.kind,
        pos: { ...p.pos },
        facing: p.facing,
        yaw: p.yaw,
        pose: p.pose,
        poseT: Math.max(0, (now - p.poseSince) / 1000),
        mood: p.mood,
        ...(p.seated ? { seated: true } : {}),
        ...(p.chip ? { chip: p.chip } : {}),
        ...(p.benchIndex !== undefined ? { benchIndex: p.benchIndex } : {}),
        visible: p.visible,
        ...(p.bubble ? { bubble: p.bubble } : {}),
        ...(carrying ? { carrying } : {})
      })
    }

    const effects = this.pendingEffects
    this.pendingEffects = []

    const busy = [...this.performers.values()].some((p) => p.current !== null || p.queue.length > 0)
    let working = 0
    for (const p of this.performers.values()) {
      if (p.kind === 'specialist' && p.visible && !this.retiredLanes.has(p.id)) working += 1
    }
    const workingCount = working + this.overflowLanes.size

    let caption = this.caption
    if (workingCount > 0) {
      caption = this.strings.specialistsOnIt(caption, workingCount)
    }

    const main = this.performers.get(MAIN_LANE)
    const tone: TheaterFrame['tone'] = this.terminal
      ? this.terminal === 'success'
        ? 'success'
        : 'failure'
      : this.waiting
        ? 'waiting'
        : busy || workingCount > 0
          ? 'working'
          : main
            ? 'working'
            : 'idle'

    return {
      characters,
      effects,
      scene: this.sceneFlags(now),
      caption,
      tone,
      busy
    }
  }

  // ─── Beat routing ──────────────────────────────────────────────────────────

  private accept(beat: Beat, now: number, instant: boolean): void {
    if (beat.caption && beat.lane === MAIN_LANE) this.caption = beat.caption

    switch (beat.kind) {
      case 'sparkle':
        if (!instant) this.effect('sparkle', STATIONS.desk.standAt)
        return
      case 'tool_done': {
        this.closeOpenAct(beat.lane)
        if (beat.error) {
          const p = this.performers.get(beat.lane)
          if (p) {
            this.effect('poof', p.pos)
            this.enqueue(beat.lane, this.makeAct({ ...beat, kind: 'stumble' }, beat.lane), now, instant)
          }
        }
        return
      }
      case 'specialist_start':
        this.spawnSpecialist(beat, now, instant)
        return
      case 'specialist_done':
        this.finishSpecialist(beat, now, instant)
        return
      case 'brief_specialist':
        // A delegation is a piece of staging, not a single pose: the lead goes
        // to the door and props it open, the arrivals file in, everyone forms
        // a standup on the rug, and the brief is handed over there.
        if (!instant) {
          this.armBriefing(now)
          return
        }
        break
      case 'wait_user':
        this.waiting = true
        break
      case 'user_note':
        this.waiting = false
        if (!instant) this.effect('note', STATIONS.door.standAt)
        this.closeOpenAct(MAIN_LANE)
        break
      case 'enter':
        this.entered = true
        break
      case 'collect_mail':
        this.inboxCount = beat.count ?? 1
        break
      case 'fetch_books':
        // Books are TAKEN when the reader reaches the shelf (onArrive) and
        // RETURNED when they finish reading (finishAct) — instant catch-up
        // needs no shelf change: past reads already put their books back.
        break
      case 'stumble':
        if (!instant) this.flashNearby(beat.lane, 'surprised', now)
        break
      case 'pack':
        if (instant) this.parcelStage = 2
        // The crates the team built ARE the delivery: ferry a couple of them
        // from the pallet to the packing table before the box goes together.
        else this.queuePackFerry(now)
        break
      case 'deliver':
        this.terminal = 'success'
        this.waiting = false
        if (instant) this.parcelStage = 3
        else this.allReact('cheer')
        break
      case 'collapse':
        this.terminal = 'failure'
        this.waiting = false
        if (!instant) this.allReact('slump')
        break
      default:
        break
    }

    const lane = beat.lane
    const isSpecialist = lane !== MAIN_LANE
    if (isSpecialist && this.overflowLanes.has(lane)) return
    if (isSpecialist && !this.performers.has(lane)) {
      // Defensive: activity for a lane we never saw start — spawn quietly.
      this.spawnSpecialist({ ...beat, kind: 'specialist_start' }, now, true)
    }

    const act = this.makeAct(beat, lane)
    if (!act) return
    this.enqueue(lane, act, now, instant)
  }

  private makeAct(beat: Beat, lane: string): Act | null {
    if (lane !== MAIN_LANE) {
      const spec = SPECIALIST_SPECS[beat.kind]
      if (!spec) return null
      const bench = this.benchByLane.get(lane)
      const benchStation = BENCH_STATIONS[bench ?? 0] ?? 'bench-0'
      // Workbench work is first-come, first-served: when both pairs of hands
      // are taken the specialist keeps building at their own desk instead.
      const crowded = spec.station === 'workbench' && this.workbenchLoad(lane) >= WORKBENCH_SLOTS
      const use: ActSpec = crowded ? BENCH_FALLBACK : spec
      const station = crowded ? benchStation : (spec.station ?? benchStation)
      return {
        beatKind: beat.kind,
        station,
        pose: use.pose,
        minMs: use.minMs,
        ...(use.openEnded ? { openEnded: true } : {}),
        ...(use.seated ? { seated: true } : {}),
        ...(beat.chip ? { chip: beat.chip } : {}),
        ...(beat.count !== undefined ? { count: beat.count } : {}),
        ...(beat.error ? { error: true } : {}),
        merged: 1
      }
    }
    const spec = SPECS[beat.kind]
    if (!spec) return null
    return {
      beatKind: beat.kind,
      station: spec.station,
      pose: spec.pose,
      minMs: spec.minMs,
      ...(spec.openEnded ? { openEnded: true } : {}),
      ...(spec.seated ? { seated: true } : {}),
      ...(beat.chip ? { chip: beat.chip } : {}),
      ...(beat.caption ? { caption: beat.caption } : {}),
      ...(beat.count !== undefined ? { count: beat.count } : {}),
      ...(beat.error ? { error: true } : {}),
      merged: 1
    }
  }

  // ─── Queueing, merging, compression ────────────────────────────────────────

  private enqueue(lane: string, act: Act | null, now: number, instant: boolean): void {
    if (!act) return
    const p = this.ensurePerformer(lane, now)
    if (!p.visible && p.kind === 'specialist') {
      // Late activity after the lane already walked out — they come back
      // through the door rather than materializing at the bench.
      p.visible = true
      p.pos = { ...doorSpawnFor(0) }
      this.retiredLanes.delete(lane)
      if (!this.benchByLane.has(lane)) {
        const free = this.freeBenches.shift()
        if (free !== undefined) {
          this.benchByLane.set(lane, free)
          p.benchIndex = free
        }
      }
    }

    if (instant) {
      this.applyInstant(p, act)
      return
    }

    // Merge: same work continuing at the same station extends the current or
    // trailing act instead of restarting it.
    const tail = p.queue[p.queue.length - 1]
    const mergeInto = tail ?? p.current?.act ?? p.stash?.act ?? null
    if (
      mergeInto &&
      !STAGING_KINDS.has(mergeInto.beatKind) &&
      mergeInto.beatKind === act.beatKind &&
      mergeInto.station === act.station &&
      (mergeInto.chip ?? '').split(' ×')[0] === (act.chip ?? '')
    ) {
      mergeInto.merged += 1
      if (act.chip) mergeInto.chip = `${act.chip} ×${mergeInto.merged}`
      // More absorbed work reads as a slightly longer act (capped so a storm
      // of tool calls never pins the character to one station for minutes).
      mergeInto.minMs = Math.min(mergeInto.minMs + act.minMs * 0.35, act.minMs * 3)
      if (p.current && !tail && mergeInto === p.current.act) p.current.closed = false
      if (p.stash && mergeInto === p.stash.act) p.stash.closed = false
      return
    }

    p.queue.push(act)
    this.compress(p)
  }

  /** Keep the backlog readable: drop oldest non-milestone acts over the cap. */
  private compress(p: Performer): void {
    if (p.queue.length <= QUEUE_SOFT_CAP) return
    const kept: Act[] = []
    let toDrop = p.queue.length - QUEUE_SOFT_CAP
    for (const act of p.queue) {
      const protectedAct = MILESTONE_KINDS.has(act.beatKind) || STAGING_KINDS.has(act.beatKind)
      if (toDrop > 0 && !protectedAct && act !== p.queue[p.queue.length - 1]) {
        toDrop -= 1
        continue
      }
      kept.push(act)
    }
    p.queue = kept
  }

  private applyInstant(p: Performer, act: Act): void {
    const stand = this.standFor(act, p)
    p.pos = { ...stand.pos }
    p.facing = stand.facing
    p.yaw = stand.yaw
    p.pose = terminalPoseFor(act)
    p.seated = act.seated ?? false
    p.chip = act.chip
    p.bubble = bubbleFor(act)
    p.current = null
    p.queue = []
    p.stash = null
    p.lastActKind = act.beatKind
    if (act.beatKind === 'deliver') p.mood = 'happy'
    if (act.beatKind === 'collapse') p.mood = 'sad'
  }

  // ─── Per-tick simulation ───────────────────────────────────────────────────

  private advance(p: Performer, now: number): void {
    this.maybeInterrupt(p, now)

    if (!p.current) {
      const next = p.queue.shift()
      if (next) {
        this.startAct(p, next, now)
      } else {
        this.idle(p, now)
        return
      }
    }

    const active = p.current
    if (!active) return

    const hurry = this.hurryFactor(p)

    if (active.phase === 'walk') {
      const t = active.walkMs <= 0 ? 1 : (now - active.walkStart) / (active.walkMs / hurry)
      const { pos, facing, yaw } = pointAlong(active.walkFrom, active.path, Math.min(1, t))
      p.pos = pos
      p.facing = facing
      p.yaw = yaw
      p.pose = 'walk'
      p.seated = false
      p.bubble = undefined
      if (t >= 1) {
        active.phase = 'perform'
        active.performStart = now
        p.pos = { ...active.stand.pos }
        p.facing = active.stand.facing
        p.yaw = active.stand.yaw
        this.onArrive(p, active.act, now)
      }
      return
    }

    // Performing.
    p.pose = active.act.pose
    p.seated = active.act.seated ?? false
    p.chip = active.act.chip
    p.bubble = bubbleFor(active.act)
    // Filler yields the instant real work lines up behind it.
    if (active.act.ambient && p.queue.some((a) => !a.ambient)) {
      this.finishAct(p, active.act, now)
      return
    }
    const held = now - active.performStart
    const needed = active.act.minMs / hurry
    const canFinish = held >= needed
    const shouldFinish = active.act.openEnded
      ? canFinish && (active.closed || (!active.act.holdOpen && p.queue.length > 0))
      : canFinish
    if (shouldFinish) {
      this.finishAct(p, active.act, now)
    }
  }

  /**
   * The two things that pull somebody off a task mid-flow: a crate that has
   * finished on the bench and needs stacking, and plain fatigue. Both stash
   * the running act and hand it back when the errand ends.
   */
  private maybeInterrupt(p: Performer, now: number): void {
    const active = p.current
    if (!active || active.phase !== 'perform' || p.stash) return
    if (this.reducedMotion || this.terminal || this.waiting || !this.entered) return
    if (this.retiredLanes.has(p.id) || !p.visible) return
    if (!INTERRUPTIBLE.has(active.act.beatKind)) return

    // A crate has come together on the bench: stop, pick it up, stack it.
    // A full pallet simply stops the cycle — the bench keeps working (hammer,
    // sparks, chip) and the stack stands as the tally of what got made, until
    // packing draws it back down.
    if (
      active.act.beatKind === 'tinker' &&
      active.act.station === 'workbench' &&
      this.crateStack < CRATE_CAPACITY &&
      now - active.performStart >= CRATE_BUILD_MS
    ) {
      this.interrupt(p, haulErrand(active.act), now)
      return
    }

    if (now >= p.breakAt && now - active.performStart > 2_500 && this.stationLoad('coffee') < COFFEE_CAPACITY) {
      p.breakAt = now + FATIGUE_MS * (0.85 + p.seed * 0.5)
      p.lastErrandAt = now
      this.interrupt(p, this.breakErrand(true), now)
    }
  }

  /** Stash the running act and splice an errand in front of it. */
  private interrupt(p: Performer, acts: Act[], now: number): void {
    const active = p.current
    if (!active || acts.length === 0) return
    const served = active.phase === 'perform' ? now - active.performStart : 0
    const base = active.act
    // The caption bar must come back to what the engine is actually doing, so
    // the lead's act carries the live caption into the stash if it has none.
    const carriedCaption = p.id === MAIN_LANE && !base.caption ? { caption: this.caption } : {}
    p.stash = { act: { ...base, ...carriedCaption }, servedMs: served, closed: active.closed }
    p.current = null
    const last = acts[acts.length - 1]
    if (last) last.resumeStash = true
    p.queue.unshift(...acts)
  }

  private startAct(p: Performer, act: Act, now: number): void {
    if (!p.visible) p.visible = true
    // The label belongs to the WHOLE act, walk included — otherwise it blinks
    // out every time the job involves going somewhere (which, now that bench
    // work carries crates to the pallet, is most of the time).
    p.chip = act.chip
    const stand = this.standFor(act, p)
    const dist = pathLength(p.pos, [stand.pos])
    if (dist < 4) {
      p.pos = { ...stand.pos }
      p.facing = stand.facing
      p.yaw = stand.yaw
      p.current = {
        act,
        phase: 'perform',
        walkFrom: p.pos,
        path: [stand.pos],
        walkStart: now,
        walkMs: 0,
        performStart: now,
        closed: false,
        stand
      }
      this.onArrive(p, act, now)
      return
    }
    const path = findPath(p.pos, stand.pos)
    const walkMs = (pathLength(p.pos, path) / WALK_SPEED_PX_S) * 1000
    p.current = {
      act,
      phase: 'walk',
      walkFrom: { ...p.pos },
      path,
      walkStart: now,
      walkMs,
      performStart: now,
      closed: false,
      stand
    }
    if (act.caption && p.id === MAIN_LANE) this.caption = act.caption
  }

  private finishAct(p: Performer, act: Act, now: number): void {
    p.current = null
    p.lastActKind = act.beatKind
    p.idleSince = now
    p.bubble = undefined
    if (act.beatKind === 'deliver') p.mood = 'happy'
    if (act.beatKind === 'collapse') p.mood = 'sad'
    if (act.beatKind === 'fetch_books') {
      // Reading done — the volumes go back on the shelf.
      this.booksOut = Math.max(0, this.booksOut - pulledBooks(act))
    }
    if (act.crateDelta !== undefined) {
      this.crateStack = Math.max(0, Math.min(CRATE_CAPACITY, this.crateStack + act.crateDelta))
      if (act.crateDelta > 0) this.effect('chips', STATIONS.crates.at)
    }
    // A specialist wrapping up a stint at their own desk leaves paper behind.
    if (p.kind === 'specialist' && p.benchIndex !== undefined && OUTPUT_KINDS.has(act.beatKind)) {
      this.bumpBenchOutput(p.benchIndex)
    }
    if (act.beatKind === 'huddle' && p.id === MAIN_LANE) this.disperseHuddle(now)
    if (act.beatKind === 'exit') {
      // Off the set — the door closes behind them.
      p.visible = false
      p.seated = false
      p.stash = null
    }
    if (act.resumeStash) this.resumeStash(p)
    // Hand the label straight to whatever comes next (the resumed act, the
    // next leg of a haul) so it doesn't blink for the frame in between.
    p.chip = p.queue[0]?.chip
  }

  /** Put the interrupted act back at the head of the queue, time already served. */
  private resumeStash(p: Performer): void {
    const stash = p.stash
    p.stash = null
    if (!stash) return
    const remaining = Math.max(900, stash.act.minMs - stash.servedMs)
    p.queue.unshift({ ...stash.act, minMs: remaining, ...(stash.closed ? { openEnded: false } : {}) })
  }

  private onArrive(p: Performer, act: Act, now: number): void {
    if (act.caption && p.id === MAIN_LANE) this.caption = act.caption
    switch (act.beatKind) {
      case 'stumble':
        this.effect('papers', p.pos)
        break
      case 'fetch_books':
        // The reader reaches the shelf and pulls DOWN the volumes they need —
        // scaled to how much knowledge the run actually consulted. They go
        // back when the read finishes (finishAct), like a real library.
        this.booksOut = Math.min(6, this.booksOut + pulledBooks(act))
        break
      case 'pack':
        this.parcelStage = 2
        break
      case 'deliver':
        // Parcel onto the belt → truck; confetti as it ships.
        this.parcelStage = 3
        this.shippedAtMs = now
        this.effect('confetti', STATIONS.delivery.standAt)
        this.effect('confetti', HUDDLE_CENTER)
        break
      case 'collect_mail':
        this.inboxCount = 0
        break
      case 'stretch':
      case 'break':
        // A coffee resets the clock, whoever poured it and why.
        p.breakAt = now + FATIGUE_MS * (0.85 + p.seed * 0.5)
        break
      case 'specialist_start':
        // They came back from the standup with the brief in hand and set it
        // down on their desk.
        if (act.carry === 'folder' && p.benchIndex !== undefined) this.bumpBenchOutput(p.benchIndex)
        break
      case 'report': {
        // Handing the finished work back to the lead, in person.
        this.effect('note', p.pos)
        const lead = this.performers.get(MAIN_LANE)
        if (lead) {
          this.flashMood(lead, 'happy', now)
          lead.socialUntil = now + act.minMs
          lead.socialWith = p.id
        }
        break
      }
      case 'chat': {
        const other = act.withLane ? this.performers.get(act.withLane) : undefined
        if (other?.visible) {
          other.socialUntil = now + act.minMs
          other.socialWith = p.id
        }
        break
      }
      default:
        break
    }
  }

  private idle(p: Performer, now: number): void {
    if (!p.visible) return
    // Terminal poses hold forever.
    if (p.lastActKind === 'collapse' || (this.terminal === 'failure' && p.lastActKind === 'cheer')) {
      p.pose = 'droop'
      return
    }
    if (p.lastActKind === 'deliver' || (this.terminal === 'success' && p.lastActKind === 'cheer')) {
      p.pose = 'idle'
      return
    }
    if (this.queueWander(p, now)) return

    if (p.kind === 'specialist') {
      const bench = this.benchByLane.get(p.id)
      const station = BENCH_STATIONS[bench ?? 0] ?? 'bench-0'
      p.pose = this.retiredLanes.has(p.id) ? 'idle' : this.ambientPose(now, (bench ?? 0) + 1, p, station)
      // Drift back to their bench after lingering at a shared station. Note
      // `idleSince` only resets when a drift-back is actually queued — it is
      // also the "how long has this been quiet" clock the errand system reads,
      // and resetting it every check would keep it pinned at zero forever.
      if (now - p.idleSince > SPECIALIST_SETTLE_MS && !this.retiredLanes.has(p.id)) {
        if (pathLength(p.pos, [STATIONS[station].standAt]) > 8) {
          p.queue.push({ beatKind: 'sit_desk', station, pose: 'idle', seated: true, minMs: 400, merged: 1 })
          p.idleSince = now
        }
      }
      return
    }
    // Waiting at the window is an open-ended pose, not idleness.
    if (p.lastActKind === 'wait_user') {
      p.pose = 'wait'
      p.bubble = 'question'
      return
    }
    p.pose = this.ambientPose(now, 0, p, 'desk')
    // Drift back to the desk chair after lingering elsewhere — but not with a
    // packed parcel on the table: the delivery is seconds away and walking to
    // the desk and straight back reads as dithering.
    if (this.parcelStage >= 2) return
    if (!this.terminal && now - p.idleSince > SETTLE_AFTER_MS) {
      const desk = STATIONS.desk
      if (pathLength(p.pos, [desk.standAt]) > 8) {
        p.queue.push({ beatKind: 'sit_desk', station: 'desk', pose: 'idle', seated: true, minMs: 400, merged: 1 })
        p.idleSince = now
      }
    }
  }

  /**
   * Between-beats presentation while the run is still going. Mid-run the agent
   * is never truly idle — the engine is thinking, writing or waiting on a tool
   * between the events we've received (and older engines only report a block
   * when it FINISHES, so gaps can span minutes). A character parked blankly at
   * the desk reads as "nothing is happening"; a settled character quietly
   * rotating through desk work (leafing pages, mulling, jotting) reads as the
   * truth: still at work. Falls back to plain idle before the run enters,
   * after terminals, and away from the character's own workspot.
   */
  private ambientPose(now: number, laneSalt: number, p: Performer, station: StationId): PoseId {
    if (this.terminal || this.waiting || !this.entered) return 'idle'
    if (pathLength(p.pos, [STATIONS[station].standAt]) > 8) return 'idle'
    p.seated = true
    // Deterministic slow rotation, desynced per lane so benches don't move in
    // lockstep. Order starts with 'read' — the most neutral "at work" pose.
    const slot = Math.floor((now + laneSalt * 5_300) / AMBIENT_SLOT_MS) % AMBIENT_POSES.length
    return AMBIENT_POSES[slot] ?? 'read'
  }

  private hurryFactor(p: Performer): number {
    const depth = p.queue.length
    if (depth > 5) return 2.2
    if (depth > 2) return 1.6
    return 1
  }

  // ─── Errands: coffee, hauling, wandering ───────────────────────────────────

  /**
   * Stretch where you stand, walk to the bar, drink it, walk back. `narrate`
   * is on only when the break INTERRUPTED something: the stashed act carries
   * the real caption back when it resumes, so the bar never ends up as the
   * last word on what the engine is doing.
   */
  private breakErrand(narrate: boolean): Act[] {
    return [
      { beatKind: 'break', station: 'desk', pose: 'yawn', minMs: 1200, inPlace: true, merged: 1 },
      {
        beatKind: 'break',
        station: 'coffee',
        pose: 'sip',
        minMs: 3200,
        chip: this.strings.breakChip,
        ...(narrate ? { caption: this.strings.coffeeBreak } : {}),
        merged: 1
      }
    ]
  }

  /**
   * A lull in a run that is still going. Nobody stands perfectly still for two
   * minutes: they get water, stare out of the window, sketch on the planning
   * board, or wander over for a word with whoever is nearest.
   */
  private queueWander(p: Performer, now: number): boolean {
    if (this.reducedMotion || !this.entered || this.terminal || this.waiting) return false
    if (!p.visible || this.retiredLanes.has(p.id) || p.stash) return false
    if (p.queue.length > 0 || p.current) return false
    if (now - p.idleSince < ERRAND_IDLE_MS) return false

    // A break that came due during a quiet stretch needs no interruption —
    // they simply get up and go.
    if (now >= p.breakAt && this.stationLoad('coffee') < COFFEE_CAPACITY) {
      p.breakAt = now + FATIGUE_MS * (0.85 + p.seed * 0.5)
      p.lastErrandAt = now
      p.queue.push(...this.breakErrand(false))
      return true
    }

    const gap = ERRAND_GAP_MS * (0.75 + p.seed * 0.7) * (p.kind === 'main' ? 1.5 : 1)
    if (now - p.lastErrandAt < gap) return false
    if (this.wanderers() >= MAX_WANDERERS) return false

    p.lastErrandAt = now
    const acts = this.ambientErrand(p)
    if (!acts) return false
    p.queue.push(...acts)
    return true
  }

  private ambientErrand(p: Performer): Act[] | null {
    const pick = (p.errandTick + Math.floor(p.seed * 5)) % 5
    p.errandTick += 1
    switch (pick) {
      case 0:
        if (this.stationLoad('cooler') > 0) return null
        return [{ beatKind: 'errand', station: 'cooler', pose: 'drink', minMs: 2600, ambient: true, merged: 1 }]
      case 1:
        return [{ beatKind: 'errand', station: 'window', pose: 'wait', minMs: 2800, ambient: true, merged: 1 }]
      case 2:
        if (this.stationLoad('whiteboard') > 0) return null
        return [{ beatKind: 'errand', station: 'whiteboard', pose: 'write', minMs: 3200, ambient: true, merged: 1 }]
      case 3: {
        const other = this.chatTarget(p)
        if (!other) return null
        return [
          {
            beatKind: 'chat',
            station: 'desk',
            pose: 'chat',
            minMs: 3400,
            ambient: true,
            withLane: other,
            merged: 1
          }
        ]
      }
      default:
        return [
          { beatKind: 'errand', station: 'desk', pose: 'yawn', minMs: 1400, inPlace: true, ambient: true, merged: 1 }
        ]
    }
  }

  /** Somebody settled, in the room, not already being talked at. */
  private chatTarget(p: Performer): string | null {
    const others = [...this.performers.values()].filter(
      (o) =>
        o.id !== p.id &&
        o.visible &&
        !this.retiredLanes.has(o.id) &&
        o.current === null &&
        o.socialUntil === 0 &&
        o.pos.x > 1.9
    )
    if (others.length === 0) return null
    return others[p.errandTick % others.length]?.id ?? null
  }

  /** Take a couple of the crates the team built over to the packing table. */
  private queuePackFerry(now: number): void {
    const trips = Math.min(2, this.crateStack)
    for (let i = 0; i < trips; i++) {
      this.enqueue(
        MAIN_LANE,
        { beatKind: 'haul', station: 'crates', pose: 'lift', minMs: 800, carry: 'crate', crateDelta: -1, merged: 1 },
        now,
        false
      )
      this.enqueue(
        MAIN_LANE,
        { beatKind: 'haul', station: 'delivery', pose: 'place', minMs: 800, carry: 'crate', merged: 1 },
        now,
        false
      )
    }
  }

  // ─── Specialists ───────────────────────────────────────────────────────────

  /**
   * A delegation, staged: the lead walks over and props the door open, the
   * specialists file in past them, everybody forms a circle on the rug, and
   * the brief is handed over there before anyone sits down.
   */
  private armBriefing(now: number): void {
    if (this.briefingArmed) return
    this.briefingArmed = true
    this.huddleDeadline = now + HUDDLE_TIMEOUT_MS
    const main = this.ensurePerformer(MAIN_LANE, now)
    const acts: Act[] = [
      {
        beatKind: 'hold_door',
        station: 'door',
        pose: 'hold_door',
        minMs: 1800,
        caption: this.strings.welcomingSpecialists,
        merged: 1
      },
      {
        beatKind: 'huddle',
        station: 'huddle',
        pose: 'brief',
        minMs: 3200,
        caption: this.strings.standupBriefing,
        carry: 'folder',
        merged: 1
      }
    ]
    if (main.stash) {
      // Mid-errand (a crate in their hands, a coffee in progress): let them
      // finish it. Splicing in front would send them to the door carrying a
      // half-delivered crate.
      main.queue.push(...acts)
      return
    }
    if (main.current && INTERRUPTIBLE.has(main.current.act.beatKind)) {
      this.interrupt(main, acts, now)
      return
    }
    main.queue.unshift(...acts)
  }

  private spawnSpecialist(beat: Beat, now: number, instant: boolean): void {
    const lane = beat.lane
    const existing = this.performers.get(lane)
    if (existing?.visible) return
    let bench = this.benchByLane.get(lane)
    if (bench === undefined) {
      const free = this.freeBenches.shift()
      if (free === undefined) {
        this.overflowLanes.add(lane)
        return
      }
      bench = free
      this.benchByLane.set(lane, bench)
    }
    const benchStation = BENCH_STATIONS[bench] ?? 'bench-0'
    const p = this.ensurePerformer(lane, now)
    p.benchIndex = bench
    p.visible = true
    p.chip = beat.chip
    this.retiredLanes.delete(lane)
    this.benchOutput[bench] = 0
    const joiningStandup = this.briefingArmed && !instant
    const arriveAct: Act = {
      beatKind: 'specialist_start',
      station: benchStation,
      pose: 'idle',
      seated: true,
      minMs: 500,
      ...(beat.chip ? { chip: beat.chip } : {}),
      // They walk back from the standup with the brief and set it on the desk.
      ...(joiningStandup ? { carry: 'folder' as CarriedItem } : {}),
      merged: 1
    }
    if (instant) {
      this.applyInstant(p, arriveAct)
      return
    }
    // Walk in from the street: through the door, across the floor, take a seat.
    p.pos = { ...doorSpawnFor(bench) }
    p.facing = 1
    p.yaw = Math.PI / 2
    p.pose = 'walk'
    if (joiningStandup) {
      this.huddleLanes.add(lane)
      // Held open-ended so the whole circle breaks up together, the moment the
      // lead finishes briefing — not whenever each person's timer happens to
      // run out.
      p.queue.unshift({
        beatKind: 'huddle',
        station: 'huddle',
        pose: 'listen',
        minMs: 2000,
        openEnded: true,
        holdOpen: true,
        merged: 1
      })
      p.queue.push(arriveAct)
      return
    }
    p.queue.unshift(arriveAct)
  }

  /** Break up the standup: everybody's listen act closes on the same frame. */
  private disperseHuddle(now: number): void {
    this.briefingArmed = false
    for (const lane of this.huddleLanes) {
      const o = this.performers.get(lane)
      if (!o) continue
      if (o.current?.act.beatKind === 'huddle') o.current.closed = true
      for (const a of o.queue) if (a.beatKind === 'huddle') a.openEnded = false
      this.flashMood(o, 'happy', now)
    }
    this.huddleLanes.clear()
  }

  private finishSpecialist(beat: Beat, now: number, instant: boolean): void {
    const lane = beat.lane
    if (this.overflowLanes.has(lane)) {
      this.overflowLanes.delete(lane)
      return
    }
    const p = this.performers.get(lane)
    if (!p) return
    p.queue = []
    if (p.current) p.current = null
    p.stash = null
    p.lastActKind = 'specialist_done'
    p.idleSince = now
    p.chip = beat.chip
    this.retiredLanes.add(lane)
    this.huddleLanes.delete(lane)
    // Their bench frees the moment they stand up — a newly hired specialist
    // may take it while this one is still walking out.
    const bench = this.benchByLane.get(lane)
    const benchStation = BENCH_STATIONS[bench ?? 0] ?? 'bench-0'
    if (bench !== undefined) {
      this.benchByLane.delete(lane)
      if (!this.freeBenches.includes(bench)) this.freeBenches.push(bench)
      this.freeBenches.sort((a, b) => a - b)
    }
    if (beat.error) {
      p.mood = 'sad'
      p.moodUntil = 0
      if (!instant) this.effect('poof', p.pos)
    } else {
      p.mood = 'happy'
      p.moodUntil = 0
      if (!instant) this.effect('note', p.pos)
    }
    if (instant) {
      p.visible = false
      return
    }
    // Stand up (a beat of celebration or a slump)…
    p.queue.push({
      beatKind: 'specialist_done',
      station: benchStation,
      pose: beat.error ? 'droop' : 'celebrate',
      minMs: beat.error ? 1300 : 1100,
      ...(beat.chip ? { chip: beat.chip } : {}),
      merged: 1
    })
    // …hand the work back to the lead in person (a failed stint slinks out
    // instead — there is nothing to hand over)…
    if (!beat.error && !this.terminal) {
      p.queue.push({
        beatKind: 'report',
        station: 'desk',
        pose: 'brief',
        minMs: 1500,
        carry: 'folder',
        withLane: MAIN_LANE,
        merged: 1
      })
    }
    // …then out through the door.
    p.queue.push({ beatKind: 'exit', station: 'door', pose: 'idle', minMs: 150, merged: 1 })
  }

  // ─── Reactions ─────────────────────────────────────────────────────────────

  /** The terminal lands on the whole room, not just the protagonist. */
  private allReact(kind: 'cheer' | 'slump'): void {
    for (const p of this.performers.values()) {
      if (!p.visible || p.id === MAIN_LANE) continue
      p.queue = []
      p.current = null
      p.stash = null
      p.mood = kind === 'cheer' ? 'happy' : 'sad'
      p.moodUntil = 0
      p.queue.push({
        beatKind: 'cheer',
        station: 'desk',
        pose: kind === 'cheer' ? 'celebrate' : 'droop',
        minMs: 3000,
        inPlace: true,
        merged: 1
      })
    }
  }

  /** Heads turn when something goes wrong nearby. */
  private flashNearby(lane: string, mood: Mood, now: number): void {
    const source = this.performers.get(lane)
    if (!source) return
    for (const p of this.performers.values()) {
      if (!p.visible) continue
      if (Math.hypot(p.pos.x - source.pos.x, p.pos.y - source.pos.y) > 4) continue
      this.flashMood(p, mood, now)
    }
  }

  private flashMood(p: Performer, mood: Mood, now: number): void {
    if (this.terminal) return
    p.mood = mood
    p.moodUntil = now + MOOD_FLASH_MS
  }

  /** Being talked at: turn toward the speaker and answer with a bubble. */
  private applySocial(p: Performer, now: number): void {
    if (p.socialUntil === 0) return
    if (now >= p.socialUntil) {
      p.socialUntil = 0
      p.socialWith = null
      return
    }
    if (p.current?.phase === 'walk') return
    const other = p.socialWith ? this.performers.get(p.socialWith) : undefined
    if (!other?.visible) return
    const f = faceToward(p.pos, other.pos)
    p.facing = f.facing
    p.yaw = f.yaw
    p.bubble = 'speech'
  }

  // ─── Plumbing ──────────────────────────────────────────────────────────────

  private ensurePerformer(lane: string, now: number): Performer {
    let p = this.performers.get(lane)
    if (p) return p
    const isMain = lane === MAIN_LANE
    const seed = hashLane(lane)
    p = {
      id: lane,
      kind: isMain ? 'main' : 'specialist',
      pos: isMain ? { ...DOOR_SPAWN } : { ...doorSpawnFor(0) },
      facing: 1,
      yaw: Math.PI / 2,
      pose: 'idle',
      poseSince: now,
      seated: false,
      mood: 'neutral',
      moodUntil: 0,
      visible: isMain,
      current: null,
      queue: [],
      idleSince: now,
      lastActKind: null,
      seed,
      stash: null,
      breakAt: now + FATIGUE_MS * (0.7 + seed * 0.6),
      lastErrandAt: now,
      errandTick: 0,
      socialUntil: 0,
      socialWith: null
    }
    this.performers.set(lane, p)
    return p
  }

  private standFor(act: Act, p: Performer): { pos: GridPoint; facing: 1 | -1; yaw: number } {
    // Reactions and stretches happen in place — nobody walks somewhere to
    // stumble, yawn or cheer.
    if (act.inPlace || act.beatKind === 'stumble') {
      return { pos: { ...p.pos }, facing: p.facing, yaw: p.yaw }
    }
    if (act.beatKind === 'huddle') {
      const slot = p.kind === 'main' ? 0 : 1 + ((p.benchIndex ?? 0) % (HUDDLE_RING - 1))
      const pos = huddleSpot(slot, HUDDLE_RING)
      return { pos, ...faceToward(pos, HUDDLE_CENTER) }
    }
    if (act.beatKind === 'report' || act.beatKind === 'chat') {
      const target = act.withLane ? this.performers.get(act.withLane) : undefined
      if (target?.visible) {
        const pos = besideSpot(target.pos, p.pos)
        return { pos, ...faceToward(pos, target.pos) }
      }
      const s = STATIONS.desk
      return { pos: s.standAt, facing: s.facing, yaw: s.yaw }
    }
    if (act.beatKind === 'brief_specialist') {
      // Stand beside the first occupied (or first) bench, facing it (+x).
      const occupied = [...this.benchByLane.values()].sort((a, b) => a - b)[0] ?? 0
      return { pos: benchBriefSpot(occupied), facing: 1, yaw: Math.PI / 2 }
    }
    if (act.beatKind === 'hold_door') {
      // Just inside the doorway, turned back into the room to wave them past.
      return { pos: { x: 1.35, y: 6.5 }, facing: -1, yaw: -Math.PI / 2 }
    }
    if (act.beatKind === 'exit') {
      // Walk out past the doorway to the street spot, facing away.
      const exiting = [...this.performers.values()].filter(
        (o) => o.id !== p.id && o.current?.act.beatKind === 'exit'
      ).length
      return { pos: doorSpawnFor(exiting), facing: -1, yaw: -Math.PI / 2 }
    }
    const s = STATIONS[act.station]
    // Shared stations (library shelf, workbench, coffee bar) fan simultaneous
    // visitors out so they stand shoulder to shoulder instead of inside each
    // other.
    if (act.station === 'shelf' || act.station === 'coffee' || act.station === 'workbench') {
      const others = [...this.performers.values()].filter(
        (o) => o.id !== p.id && o.current !== null && o.current.act.station === act.station
      ).length
      return { pos: stationSlot(act.station, others), facing: s.facing, yaw: s.yaw }
    }
    return { pos: s.standAt, facing: s.facing, yaw: s.yaw }
  }

  private closeOpenAct(lane: string): void {
    const p = this.performers.get(lane)
    if (!p) return
    if (p.current?.act.openEnded) p.current.closed = true
    // The work may be parked in the stash while its owner fetches a coffee or
    // stacks a crate — closing it there matters, or it restarts open-ended.
    if (p.stash?.act.openEnded) p.stash.closed = true
    const tail = p.queue[p.queue.length - 1]
    if (tail?.openEnded) {
      // Will expire at minMs — flag by clearing openEnded.
      tail.openEnded = false
    }
  }

  /** People currently at (or on their way to) a station. */
  private stationLoad(station: StationId): number {
    let n = 0
    for (const p of this.performers.values()) {
      if (!p.visible) continue
      if (p.current?.act.station === station || p.queue.some((a) => a.station === station)) n += 1
    }
    return n
  }

  private workbenchLoad(exclude: string): number {
    let n = 0
    for (const p of this.performers.values()) {
      if (p.id === exclude || !p.visible) continue
      if (p.current?.act.station === 'workbench' || p.queue.some((a) => a.station === 'workbench')) n += 1
    }
    return n
  }

  private wanderers(): number {
    let n = 0
    for (const p of this.performers.values()) {
      if (p.current?.act.ambient || p.queue.some((a) => a.ambient)) n += 1
    }
    return n
  }

  private bumpBenchOutput(bench: number): void {
    const cur = this.benchOutput[bench] ?? 0
    this.benchOutput[bench] = Math.min(3, cur + 1)
  }

  private effect(kind: EffectFrame['kind'], at: GridPoint): void {
    this.effectSeq += 1
    this.pendingEffects.push({ id: `fx-${this.effectSeq}`, kind, at: { ...at } })
  }

  private sceneFlags(now: number): SceneFlags {
    const main = this.performers.get(MAIN_LANE)
    const mainAct = main?.current?.act
    // The door swings for WHOEVER is near it — the main walking in, a reply
    // note arriving, specialists filing in or out — and stays wide while
    // somebody is physically holding it.
    let doorOpen = false
    let doorHeld = false
    let craftProgress = 0
    for (const p of this.performers.values()) {
      if (!p.visible) continue
      if (inDoorZone(p.pos)) doorOpen = true
      if (p.pose === 'hold_door') {
        doorHeld = true
        doorOpen = true
      }
      const a = p.current
      if (
        a &&
        a.phase === 'perform' &&
        a.act.beatKind === 'tinker' &&
        a.act.station === 'workbench' &&
        this.crateStack < CRATE_CAPACITY &&
        !this.terminal
      ) {
        craftProgress = Math.max(craftProgress, Math.min(1, (now - a.performStart) / CRATE_BUILD_MS))
      }
    }
    return {
      doorOpen,
      doorHeld,
      lampState: this.terminal === 'failure' ? 'alarm' : this.entered ? 'warm' : 'off',
      parcelStage: this.parcelStage,
      inboxCount: this.inboxCount,
      booksOut: this.booksOut,
      benchOverflow: this.overflowLanes.size,
      consoleActive: mainAct?.beatKind === 'call' && main?.current?.phase === 'perform',
      cabinetOpen: mainAct?.beatKind === 'file' && main?.current?.phase === 'perform',
      celebrating: mainAct?.beatKind === 'deliver',
      shippedAtMs: this.shippedAtMs,
      crateStack: this.crateStack,
      craftProgress,
      benchOutput: [...this.benchOutput]
    }
  }
}

const MILESTONE_KINDS = new Set<BeatKind>([
  'enter',
  'wait_user',
  'user_note',
  'pack',
  'deliver',
  'collapse',
  'brief_specialist',
  'stumble',
  'specialist_start',
  'exit'
])

/** Acts that leave paper behind on a specialist's own desk. */
const OUTPUT_KINDS = new Set<BeatKind>(['think', 'write', 'tinker', 'call', 'file'])

/**
 * The crate cycle: take the finished crate off the bench where you stand, walk
 * it over to the pallet and stack it. The work chip rides along so the label
 * doesn't blink out mid-carry.
 */
function haulErrand(source: Act): Act[] {
  const chip = source.chip ? { chip: source.chip } : {}
  return [
    {
      beatKind: 'haul',
      station: 'workbench',
      pose: 'lift',
      minMs: 950,
      inPlace: true,
      carry: 'crate',
      ...chip,
      merged: 1
    },
    {
      beatKind: 'haul',
      station: 'crates',
      pose: 'place',
      minMs: 900,
      carry: 'crate',
      crateDelta: 1,
      ...chip,
      merged: 1
    }
  ]
}

/** How many shelf volumes one read pulls — proportional to the knowledge
 *  count on the beat, capped so the shelf never empties visually. */
function pulledBooks(act: Act): number {
  return Math.min(4, Math.max(1, act.count ?? act.merged))
}

function bubbleFor(act: Act): CharacterFrame['bubble'] {
  if (act.beatKind === 'think') return 'think'
  if (act.beatKind === 'brief_specialist' || act.beatKind === 'call') return 'speech'
  if (act.beatKind === 'chat' || act.beatKind === 'report') return 'speech'
  if (act.beatKind === 'huddle' && act.pose === 'brief') return 'speech'
  if (act.beatKind === 'wait_user') return 'question'
  return undefined
}

function terminalPoseFor(act: Act): PoseId {
  if (act.beatKind === 'collapse') return 'droop'
  if (act.beatKind === 'wait_user') return 'wait'
  if (act.beatKind === 'deliver') return 'idle'
  return 'idle'
}

/** Stable 0..1 salt per lane id — desyncs breaks, errands and gait phase. */
function hashLane(id: string): number {
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 997
  return h / 997
}
